import { supabase } from "@/integrations/supabase/client";
import {
  extractSupabaseEdgeFunctionError,
  extractSupabaseEdgeFunctionErrorAsync,
  type EdgeFunctionInvokeError,
} from "@/utils/edgeFunctionErrors";

const AUDIO_TRANSCRIPTION_TIMEOUT_MS = 45000;

export interface AudioFeatureAnalysisResult {
  durationSeconds: number | null;
  anchorCount: number;
}

export interface QueuedVideoTranscriptionResult {
  transcriptUploadId: string;
  videoJobId: string;
  status: string | null;
}

export async function requestAudioTranscription({
  audioPath,
  leaderId,
  retainAudioForProcessing = true,
}: {
  audioPath: string;
  leaderId: string;
  /** Set to false for callers that never trigger process-audio-features (e.g. reflection path).
   *  Defaults to true so the main upload pipeline continues to retain audio for feature extraction. */
  retainAudioForProcessing?: boolean;
}) {
  const sessionResult = await supabase.auth.getSession().catch(() => null);
  const accessToken = sessionResult?.data.session?.access_token;
  const invokePromise = supabase.functions.invoke("transcribe-audio", {
    body: {
      storage_path: audioPath,
      leader_id: leaderId,
      retain_audio_for_processing: retainAudioForProcessing,
    },
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Audio transcription timed out. You can paste a transcript manually and continue."));
    }, AUDIO_TRANSCRIPTION_TIMEOUT_MS);
  });

  const { data, error } = await Promise.race([invokePromise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });

  if (error) {
    throw new Error(await extractSupabaseEdgeFunctionErrorAsync(data, error as EdgeFunctionInvokeError, "Audio transcription failed"));
  }

  return data?.text || null;
}

export async function requestAudioFeatureProcessing({
  meetingId,
  leaderId,
  sourceFileName,
  audioPath,
}: {
  meetingId?: string;
  leaderId: string;
  sourceFileName: string;
  audioPath: string;
}): Promise<AudioFeatureAnalysisResult> {
  const { data, error } = await supabase.functions.invoke("process-audio-features", {
    body: {
      meeting_id: meetingId ?? null,
      leader_id: leaderId,
      sourceFileName,
      storage_path: audioPath,
    },
  });

  if (error) {
    throw error;
  }

  return {
    durationSeconds: data?.duration_seconds ?? null,
    anchorCount: Array.isArray(data?.anchors) ? data.anchors.length : 0,
  };
}

export async function requestQueuedVideoTranscription({
  videoPath,
  leaderId,
  meetingId,
  consentText,
  sourceFileName,
}: {
  videoPath: string;
  leaderId: string;
  meetingId?: string;
  consentText: string;
  sourceFileName: string;
}): Promise<QueuedVideoTranscriptionResult> {
  const { data, error } = await supabase.functions.invoke("enqueue-transcript-video-job", {
    body: {
      video_path: videoPath,
      meeting_id: meetingId ?? null,
      consent_text: consentText,
      source_file_name: sourceFileName,
      leader_id: leaderId,
    },
  });

  if (error) {
    throw new Error(extractSupabaseEdgeFunctionError(data, error as EdgeFunctionInvokeError, "Large video queueing failed"));
  }

  return {
    transcriptUploadId: data?.transcript_upload_id,
    videoJobId: data?.video_job_id,
    status: data?.status ?? null,
  };
}
